import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Stack,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getActiveHotelId } from "../../Utils/Utils";
import { springBaseUrl } from "../CalendarPage/constants/AmplitudeConstants";

const AUTH_HEADER = {
  Authorization: localStorage.getItem("token"),
};
const HOTEL_DOCS_URL =
  "https://prodimagesbucket.blob.core.windows.net/hotel-docs";

const HotelKycForm = ({ checkFormStatus }) => {
  const hotelId = getActiveHotelId();
  const [steps, setSteps] = useState({
    otpSent: false,
    otpVerified: false,
    gstUploaded: false,
    panUploaded: false,
    formSubmitted: false,
  });
  const [gstId, setGstId] = useState("");
  const [panId, setPanId] = useState("");

  const [loading, setLoading] = useState({
    sendingOtp: false,
    verifyingOtp: false,
    uploadingGST: false,
    uploadingPAN: false,
    submitting: false,
  });

  const formik = useFormik({
    initialValues: {
      ownerName: "",
      phoneNumber: "",
      otp: "",
      lastVerifiedPhone: "",
      gstCertificate: null,
      panCard: null,
      accountHolderName: "",
      ifscCode: "",
      accountNumber: "",
    },
    validationSchema: Yup.object({
      ownerName: Yup.string().required("Required"),
      phoneNumber: Yup.string()
        .matches(/^\d{10}$/, "Must be a 10-digit number")
        .required("Required"),
      otp: Yup.string().when("steps.otpSent", {
        is: true,
        then: Yup.string().required("Enter OTP"),
      }),
      gstCertificate: Yup.mixed().required("Required"),
      panCard: Yup.mixed().required("Required"),
      accountHolderName: Yup.string().required("Required"),
      ifscCode: Yup.string().required("Required"),
      accountNumber: Yup.string().required("Required"),
    }),
    onSubmit: () => submitHotelKyc(),
  });

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (err) => reject(err);
    });

  const sendOtp = async () => {
    setLoading((l) => ({ ...l, sendingOtp: true }));
    try {
      await axios.get(
        springBaseUrl + `core/api/v1/hotelVerification/send-otp`,
        {
          params: {
            hotelId: hotelId,
            mobileNumber: formik.values.phoneNumber,
          },
          headers: AUTH_HEADER,
        }
      );
      setSteps((s) => ({ ...s, otpSent: true }));
    } catch (err) {
      console.error(err);
      alert("Failed to send OTP");
    } finally {
      setLoading((l) => ({ ...l, sendingOtp: false }));
    }
  };

  const verifyOtp = async () => {
    setLoading((l) => ({ ...l, verifyingOtp: true }));
    try {
      await axios.get(
        springBaseUrl + `core/api/v1/hotelVerification/verify-otp`,
        {
          params: {
            hotelId: hotelId,
            mobileNumber: formik.values.phoneNumber,
            otp: formik.values.otp,
          },
          headers: AUTH_HEADER,
        }
      );
      setSteps((s) => ({ ...s, otpVerified: true }));
      formik.setFieldValue("lastVerifiedPhone", formik.values.phoneNumber);
    } catch (err) {
      console.error(err);
      alert("OTP verification failed");
    } finally {
      setLoading((l) => ({ ...l, verifyingOtp: false }));
    }
  };

  const uploadDocument = async (file, type) => {
    setLoading((l) => ({
      ...l,
      [type === "gst" ? "uploadingGST" : "uploadingPAN"]: true,
    }));
    try {
      const base64 = await toBase64(file);
      const response = await axios.post(
        springBaseUrl + `core/api/v1/image/upload/hotelDocument`,
        {
          photoId: `${hotelId}_${type}`,
          imageData: base64.split(",")[1],
          imageType: file.name.split(".").pop(),
          orderId: hotelId,
        },
        { headers: AUTH_HEADER }
      );
      setSteps((s) => ({
        ...s,
        [type === "gst" ? "gstUploaded" : "panUploaded"]: true,
      }));

      const photoId = response?.data?.photoId;
      type === "gst" ? setGstId(photoId) : setPanId(photoId);
    } catch (err) {
      console.error(err);
      alert(`Failed to upload ${type.toUpperCase()}`);
    } finally {
      setLoading((l) => ({
        ...l,
        [type === "gst" ? "uploadingGST" : "uploadingPAN"]: false,
      }));
    }
  };

  const submitHotelKyc = async () => {
    setLoading((l) => ({ ...l, submitting: true }));
    try {
      await axios.post(
        springBaseUrl + `core/api/v1/hotelKyc`,
        {
          hotelId: hotelId,
          ownerName: formik.values.ownerName,
          phoneNumber: formik.values.phoneNumber,
          isPhoneVerified: true,
          gstCertificateUrl: `${HOTEL_DOCS_URL}/${hotelId}/${gstId}`,
          panCardUrl: `${HOTEL_DOCS_URL}/${hotelId}/${panId}`,
          accountHolderName: formik.values.accountHolderName,
          ifscCode: formik.values.ifscCode,
          accountNumber: formik.values.accountNumber,
          kycComplete: true,
        },
        { headers: AUTH_HEADER }
      );
      setSteps((s) => ({ ...s, formSubmitted: true }));
      await checkFormStatus();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading((l) => ({ ...l, submitting: false }));
    }
  };

  return (
    <Stack spacing={2} p={2}>
      <Typography variant="h6">Complete your KYC in five steps:</Typography>

      <form onSubmit={formik.handleSubmit} encType="multipart/form-data">
        <Stack spacing={2}>
          {/* Owner Name */}
          <TextField
            id="ownerName"
            name="ownerName"
            label="Business Owner Name"
            value={formik.values.ownerName}
            onChange={formik.handleChange}
            error={formik.touched.ownerName && Boolean(formik.errors.ownerName)}
            helperText={formik.touched.ownerName && formik.errors.ownerName}
            InputProps={{
              endAdornment: formik.values.ownerName && (
                <InputAdornment position="end">
                  <CheckCircleIcon color="success" />
                </InputAdornment>
              ),
            }}
          />

          {/* Phone & OTP */}
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              id="phoneNumber"
              name="phoneNumber"
              label="Phone Number"
              fullWidth
              value={formik.values.phoneNumber}
              onChange={(e) => {
                formik.handleChange(e);
                if (formik.values.lastVerifiedPhone) {
                  setSteps((s) => ({
                    ...s,
                    otpSent: false,
                    otpVerified: false,
                  }));
                  formik.setFieldValue("lastVerifiedPhone", "");
                }
              }}
              error={
                formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)
              }
              helperText={formik.errors.phoneNumber}
              InputProps={{
                endAdornment: steps.otpVerified && (
                  <InputAdornment position="end">
                    <CheckCircleIcon color="success" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              onClick={sendOtp}
              disabled={
                loading.sendingOtp ||
                !formik.values.phoneNumber.match(/^\d{10}$/)
              }
            >
              {loading.sendingOtp ? <CircularProgress size={20} /> : "Send OTP"}
            </Button>
          </Stack>

          {steps.otpSent && !steps.otpVerified && (
            <Stack direction="row" spacing={2}>
              <TextField
                id="otp"
                name="otp"
                label="Enter OTP"
                value={formik.values.otp}
                onChange={formik.handleChange}
              />
              <Button
                variant="outlined"
                onClick={verifyOtp}
                disabled={loading.verifyingOtp || !formik.values.otp}
              >
                {loading.verifyingOtp ? (
                  <CircularProgress size={20} />
                ) : (
                  "Verify OTP"
                )}
              </Button>
            </Stack>
          )}

          {/* Upload GST */}
          <Stack spacing={2}>
            <Button
              variant="outlined"
              component="label"
              endIcon={
                steps.gstUploaded ? <CheckCircleIcon color="success" /> : null
              }
            >
              {loading.uploadingGST ? (
                <CircularProgress size={20} />
              ) : (
                "Upload GST Certificate"
              )}
              <input
                type="file"
                hidden
                accept=".pdf"
                onChange={(e) => {
                  const file = e.currentTarget.files[0];
                  formik.setFieldValue("gstCertificate", file);
                  uploadDocument(file, "gst");
                }}
              />
            </Button>

            {formik.values.gstCertificate && (
              <Typography>{formik.values.gstCertificate.name}</Typography>
            )}
          </Stack>

          {/* Upload PAN */}
          <Stack spacing={2}>
            <Button
              variant="outlined"
              component="label"
              endIcon={
                steps.panUploaded ? <CheckCircleIcon color="success" /> : null
              }
            >
              {loading.uploadingPAN ? (
                <CircularProgress size={20} />
              ) : (
                "Upload PAN Card"
              )}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  formik.setFieldValue("panCard", e.currentTarget.files[0]);
                  uploadDocument(e.currentTarget.files[0], "pan");
                }}
              />
            </Button>
            {formik.values.panCard && (
              <Typography>{formik.values.panCard.name}</Typography>
            )}
          </Stack>

          {/* Bank Details */}
          {["accountHolderName", "ifscCode", "accountNumber"].map((field) => (
            <Stack key={field} direction="row" alignItems="center" spacing={1}>
              <TextField
                fullWidth
                id={field}
                name={field}
                label={field
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (str) => str.toUpperCase())}
                value={formik.values[field]}
                onChange={formik.handleChange}
                InputProps={{
                  endAdornment: formik.values[field] && (
                    <InputAdornment position="end">
                      <CheckCircleIcon color="success" />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          ))}

          {/* Submit Button */}
          <Button
            variant="contained"
            type="submit"
            disabled={
              !(
                steps.otpVerified &&
                steps.gstUploaded &&
                steps.panUploaded &&
                formik.values.accountHolderName &&
                formik.values.ifscCode &&
                formik.values.accountNumber
              ) || loading.submitting
            }
          >
            {loading.submitting ? (
              <CircularProgress size={20} />
            ) : (
              "Complete KYC Now"
            )}
          </Button>
        </Stack>
      </form>
    </Stack>
  );
};

export default HotelKycForm;
